import { useMemo, useCallback } from "react";
import {
  parse,
  format,
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import RsDateRangePicker from "./RsDateRangePicker";
import { Form, Button } from "react-bootstrap";
import Select from "react-select";
import { CancelX } from "../Icons";

/*
  Serialization format (keeps backward compatibility with older "start|end" pairs):
    "start|end|month|year;start|end|month|year;..."
  month can be 'Date Range', 'Year' or 0-11, year is '' or number
*/



const currentYear = new Date().getFullYear();
const initialRange = {
  startDate: format(startOfYear(new Date()), "yyyy-MM-dd"),
  endDate: format(endOfYear(new Date()), "yyyy-MM-dd"),
  month: "Year",
  year: currentYear,
};

function parseRanges(serialized = "") {
  if (!serialized) return [initialRange];

  return serialized
    .split(";")
    .filter(Boolean)
    .map(pair => {
      const parts = pair.split("|");
      const start = parts[0] || "";
      const end = parts[1] || "";
      const month =
        parts[2] !== undefined && parts[2] !== "" ? parts[2] : "Date Range";
      const year =
        parts[3] !== undefined && parts[3] !== "" ? Number(parts[3]) : "";
      return { startDate: start, endDate: end, month, year };
    });
}

function serializeRanges(ranges = []) {
  return ranges
    .map(r => {
      const month = r.month === undefined ? "Date Range" : r.month;
      const year = r.year === undefined ? "" : r.year;
      return `${r.startDate || ""}|${r.endDate || ""}|${month}|${year}`;
    })
    .join(";");
}

function MultiDateRangerPicker({ filterParams = {}, setFilterParams }) {
  const months = [
    { value: "Date Range", label: "Date Range" },
    { value: "Year", label: "Year" },
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
  ];

  const dateRanges = useMemo(() => parseRanges(filterParams?.dateRanges), [
    filterParams?.dateRanges,
  ]);

  const updateRanges = useCallback(
    newRanges => {
      setFilterParams({
        ...filterParams,
        dateRanges: serializeRanges(newRanges),
      });
    },
    [filterParams, setFilterParams]
  );

  const addRange = () => {
    const newRanges = [...dateRanges, initialRange];
    updateRanges(newRanges);
  };

  const removeRange = index => {
    const newRanges = dateRanges.filter((_, i) => i !== index);
    updateRanges(
      newRanges.length
        ? newRanges
        : [{ startDate: "", endDate: "", month: "Date Range", year: "" }]
    );
  };

  const clearDateRange = index => {
    const newRanges = dateRanges.map((r, i) =>
      i === index ? { ...r, startDate: "", endDate: "" } : r
    );
    updateRanges(newRanges);
  };

  const filterByDateRange = (dateArray, index) => {
    const [start, end] = dateArray || [];
    const newRanges = dateRanges.map((r, i) =>
      i === index
        ? {
            ...r,
            startDate: start ? format(start, "yyyy-MM-dd") : "",
            endDate: end ? format(end, "yyyy-MM-dd") : "",
            month: "Date Range",
            year: start ? start.getFullYear() : r.year,
          }
        : r
    );
    updateRanges(newRanges);
  };

  const handleAccountingPeriod = ({ month, year, index }) => {
    try {
      // normalize year
      let y = year;
      if (y === "" || y === undefined || y === null) {
        y = "";
      } else {
        y = Number(y);
        if (Number.isNaN(y)) y = "";
      }

      // update ranges
      if (month === "Date Range") {
        const newRanges = dateRanges.map((r, i) =>
          i === index ? { ...r, month: "Date Range", year: y } : r
        );
        updateRanges(newRanges);
        return;
      }

      if (month === "Year") {
        if (!y) {
          // set month/year but clear dates if invalid year
          const newRanges = dateRanges.map((r, i) =>
            i === index
              ? { ...r, startDate: "", endDate: "", month: "Year", year: "" }
              : r
          );
          updateRanges(newRanges);
          return;
        }
        const start = startOfYear(new Date(y, 0));
        const end = endOfYear(new Date(y, 0));
        const newRanges = dateRanges.map((r, i) =>
          i === index
            ? {
                ...r,
                startDate: format(start, "yyyy-MM-dd"),
                endDate: format(end, "yyyy-MM-dd"),
                month: "Year",
                year: y,
              }
            : r
        );
        updateRanges(newRanges);
        return;
      }

      // numeric month (0-11)
      if (typeof month === "number" || !Number.isNaN(Number(month))) {
        const m = Number(month);
        const useYear = y || new Date().getFullYear();
        const start = startOfMonth(new Date(useYear, m));
        const end = endOfMonth(new Date(useYear, m));
        const newRanges = dateRanges.map((r, i) =>
          i === index
            ? {
                ...r,
                startDate: format(start, "yyyy-MM-dd"),
                endDate: format(end, "yyyy-MM-dd"),
                month: m,
                year: useYear,
              }
            : r
        );
        updateRanges(newRanges);
      }
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      {dateRanges.map((range, i) => (
        <div
          key={i}
          className="mb-3 p-2 border rounded d-flex align-items-center"
        >
          <Form.Group className="mb-0 me-3" style={{ minWidth: 200 }}>
            <Form.Label className="mb-1">Accounting Period</Form.Label>
            <div style={{ minWidth: 200 }}>
              <Select
                classNamePrefix="form-select"
                placeholder="Accounting Period"
                isSearchable={true}
                options={months}
                value={
                  months.find(el => String(el.value) === String(range.month)) ||
                  months.find(m => m.value === "Date Range")
                }
                onChange={selected =>
                  handleAccountingPeriod({
                    month: selected?.value,
                    year: range?.year,
                    index: i,
                  })
                }
                isClearable={false}
              />
            </div>
          </Form.Group>

          <Form.Group className="mb-0 me-3" style={{ width: 110 }}>
            <Form.Label className="mb-1">Year</Form.Label>
            <Form.Control
              type="number"
              name="year"
              value={range?.year || ""}
              placeholder="Year"
              onChange={e => {
                handleAccountingPeriod({
                  month: range?.month,
                  year: e.target.value === "" ? "" : Number(e.target.value),
                  index: i,
                });
              }}
              style={{ width: "100%" }}
            />
          </Form.Group>

          <Form.Group className="mb-0 me-3" style={{ minWidth: 280 }}>
            <Form.Label className="mb-1">Date Range</Form.Label>
            <div className="position-relative">
              <RsDateRangePicker
                placement="topStart"
                value={
                  range.startDate && range.endDate
                    ? [
                        parse(range.startDate, "yyyy-MM-dd", new Date()),
                        parse(range.endDate, "yyyy-MM-dd", new Date()),
                      ]
                    : []
                }
                onClean={() => clearDateRange(i)}
                onOk={date => filterByDateRange(date, i)}
              />
            </div>
          </Form.Group>

          <div className="d-flex gap-2 ms-auto">
            <Button variant="" size="sm" onClick={() => removeRange(i)}>
              <CancelX />
            </Button>
          </div>
        </div>
      ))}

      <div className="mt-2">
        <Button onClick={addRange} size="sm" variant="light-blue">
          + Add Period
        </Button>
      </div>
    </>
  );
}

export default MultiDateRangerPicker;
